import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIos';

import AttachFilesActionButton from '../../../components/designe/ActionButtons/AttachFilesActionButton';
import SendActionButton from '../../../components/designe/ActionButtons/SendActionButton';
import AppFile from '../../../components/designe/app-file/AppFile';
import AppTextField from '../../../components/designe/app-text-field/AppTextField';
import withRouter from '../../../hooks/with-router';
import { BugReportStatus } from '../../../shared/constants';
import theme from '../../../styles/theme';
import { useStyles } from './BugReport.styles';
import BugReportMessage from './bug-report-message/BugReportMessage';
import { useBugReport } from './useBugReport';
import AppFormTextField from '../../../components/designe/app-form-text-field';
import AppFormSelectWrapper from '../../../components/designe/app-form-select-wrapper';
import AppButton from '../../../components/designe/app-button/AppButton';
import { Paths } from '../../../shared/paths';

function BugReport(props) {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const { params } = props;
  const {
    report,
    newPostFilesToUpload,
    isLoading,
    formik,
    onUploadNewPostFiles,
    onDeleteNewPostFile,
  } = useBugReport(params.reportId);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [report]);

  const fallbackTabPath = location.pathname.includes('/list/archived/')
    ? `${Paths.moderatorDashboard.bugReports()}/list/archived`
    : `${Paths.moderatorDashboard.bugReports()}/list/active`;
  const fromPath = (location.state as { from?: string } | undefined)?.from;
  const backPath = fromPath || fallbackTabPath;

  const statusOptions = [
    {
      id: BugReportStatus.responded,
      name: t(
        `moderatorDashboard.bugReportStatus_${BugReportStatus.responded}`,
      ),
    },
    {
      id: BugReportStatus.closed,
      name: t(`moderatorDashboard.bugReportStatus_${BugReportStatus.closed}`),
    },
  ];

  return (
    <Box>
      <AppButton
        variant="text"
        className={classes.backButton}
        onClick={() => navigate(backPath)}>
        <ArrowBackIcon />
        <Typography variant="h2">
          {t('adminDashboard.notifications.backToList')}
        </Typography>
      </AppButton>
      <Box className={classes.bugReportWrapper}>
        <Box className={classes.bugReportMessagesContainer} ref={containerRef}>
          {report?.messages?.map((message) => (
            <BugReportMessage
              message={message}
              key={message.id}
              reportUserId={report?.creator.id}
            />
          ))}
        </Box>
        {report?.status !== BugReportStatus.closed && (
          <Box className={classes.newMessageWrapper}>
            {!!newPostFilesToUpload.length && (
              <Box className={classes.uploadedFilesWrapper}>
                {newPostFilesToUpload.map((file) => (
                  <AppFile
                    key={file.id}
                    file={file}
                    onDelete={(file) => onDeleteNewPostFile(file.id)}
                  />
                ))}
              </Box>
            )}
            <Box className={classes.newMessageContainer}>
              <AppFormSelectWrapper
                className={classes.statusField}
                label={t('moderatorDashboard.status')}
                name="status"
                options={statusOptions}
                defaultValue={statusOptions?.find(
                  (o) => o.id === formik.values.status,
                )}
                fullWidth={false}
                showSearch={false}
                disableClear={true}
                onChange={(e) =>
                  formik.setFieldValue('status', e.target.value.id)
                }
                formik={formik}
              />
              <AppFormTextField
                label={t('moderatorDashboard.newMessage')}
                fullWidth
                multiline
                maxRows={5}
                formik={formik}
                name={'message'}
              />

              <AttachFilesActionButton
                onFileInput={(e) => onUploadNewPostFiles(e.target.files)}
              />
              <SendActionButton
                onClick={formik.submitForm}
                disabled={isLoading || !formik.values.message}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default withRouter(BugReport);
